import {
  BookOpen,
  BriefcaseBusiness,
  Building2,
  Clock3,
  KeyRound,
  MessageCircle,
  Network,
  Route,
  ShieldCheck,
  Users,
  Workflow,
} from 'lucide-vue-next'
import type { PermissionRequirement, PermissionedMenuGroup } from '@/types/permission'
import { canAccessPermissionRequirement } from '@/utils/permission'
import { toTenantAdminPath } from '@/utils/tenantRoute'

export const adminNavigationGroups: PermissionedMenuGroup[] = [
  {
    key: 'base',
    title: '基础管理',
    collapsedTitle: '基础',
    children: [
      {
        key: 'tenant',
        title: '租户管理',
        collapsedLabel: '租户',
        icon: Building2,
        route: '/admin/tenant',
        permission: 'tenant:profile:view',
      },
      {
        key: 'department',
        title: '部门管理',
        collapsedLabel: '部门',
        icon: Network,
        route: '/admin/department',
        permission: 'org:department:view',
      },
      {
        key: 'user',
        title: '用户管理',
        collapsedLabel: '用户',
        icon: Users,
        route: '/admin/user',
        permission: 'org:user:view',
      },
    ],
  },
  {
    key: 'permission',
    title: '权限管理',
    collapsedTitle: '权限',
    children: [
      {
        key: 'roles',
        title: '角色管理',
        collapsedLabel: '角色',
        icon: ShieldCheck,
        route: '/admin/roles',
        permission: 'permission:role:view',
      },
      {
        key: 'permissions',
        title: '权限分配',
        collapsedLabel: '权限',
        icon: KeyRound,
        route: '/admin/permissions',
        permission: 'permission:role:view',
      },
    ],
  },
  {
    key: 'audit',
    title: '审计日志',
    collapsedTitle: '审计',
    children: [
      {
        key: 'audits',
        title: '审计日志',
        collapsedLabel: '审计',
        icon: Clock3,
        route: '/admin/audits',
        permission: 'system:audit:view',
      },
    ],
  },
  {
    key: 'config',
    title: '配置管理',
    collapsedTitle: '配置',
    children: [
      {
        key: 'dict',
        title: '字典管理',
        collapsedLabel: '字典',
        icon: BookOpen,
        route: '/admin/dict',
        permission: 'system:dict:view',
      },
      {
        key: 'positions',
        title: '岗位库',
        collapsedLabel: '岗位',
        icon: BriefcaseBusiness,
        route: '/admin/positions',
        permission: 'org:position:view',
      },
      {
        key: 'department-positions',
        title: '部门岗位',
        collapsedLabel: '部岗',
        icon: Building2,
        route: '/admin/department-positions',
        permission: 'org:department_position:view',
      },
      {
        key: 'squads',
        title: '归属小分队',
        collapsedLabel: '小队',
        icon: Network,
        route: '/admin/squads',
        permission: 'org:squad:view',
      },
      {
        key: 'tracks',
        title: '赛道配置',
        collapsedLabel: '赛道',
        icon: Route,
        route: '/admin/tracks',
        permission: 'org:track:view',
      },
      {
        key: 'org-integration',
        title: '组织同步',
        collapsedLabel: '同步',
        icon: Workflow,
        route: '/admin/org-integration',
        permission: 'integration:external_directory:view',
      },
      {
        key: 'im',
        title: 'IM 集成',
        collapsedLabel: 'IM',
        icon: MessageCircle,
        route: '/admin/im',
        permission: 'integration:im:view',
      },
    ],
  },
]

export const adminEntryPermission: PermissionRequirement = {
  any: adminNavigationGroups.flatMap(group =>
    group.children.flatMap(item => {
      const requirement = item.permission
      if (!requirement) return []
      if (typeof requirement === 'string') return [requirement]
      if (Array.isArray(requirement)) return requirement
      return [...(requirement.any ?? []), ...(requirement.all ?? [])]
    })
  ),
}

export function getFirstAccessibleAdminRoute(
  permissions: string[],
  isAdmin = false
): string | undefined {
  for (const group of adminNavigationGroups) {
    for (const item of group.children) {
      if (item.route && canAccessPermissionRequirement(item.permission, permissions, isAdmin)) {
        return toTenantAdminPath(item.route)
      }
    }
  }

  return undefined
}

export function getFirstAccessibleAdminRouteFromStorage(fallback = '/403'): string {
  return getFirstAccessibleAdminRoute(readPermissionsFromStorage()) || fallback
}

export function filterAdminNavigationGroups(
  groups: PermissionedMenuGroup[],
  permissions: string[],
  isAdmin = false
): PermissionedMenuGroup[] {
  return groups
    .map(group => ({
      ...group,
      children: group.children.filter(item =>
        canAccessPermissionRequirement(item.permission, permissions, isAdmin)
      ),
    }))
    .filter(group => group.children.length > 0)
}

function readPermissionsFromStorage(): string[] {
  try {
    const raw = localStorage.getItem('permissions')
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}
